import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onClick
}: FAQItemProps) {
  return <div className="border-b border-cream-200 last:border-b-0" data-id="element-41">
      <button onClick={onClick} className="w-full py-6 flex items-start justify-between text-left focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 rounded-lg" aria-expanded={isOpen} data-id="element-42">
        <span className="text-lg sm:text-xl font-semibold text-navy-900 pr-4" data-id="element-43">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0 mt-1" data-id="element-44">
          <ChevronDown className="w-6 h-6 text-amber-600" data-id="element-45" />
        </motion.div>
      </button>

      <AnimatePresence data-id="element-46">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-47">
            <p className="pb-6 text-navy-600 leading-relaxed text-base sm:text-lg" data-id="element-48">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: 'Ya probé de todo: melatonina, apps, meditaciones... y nada me funcionó. ¿Por qué esto sería diferente?',
    answer: 'Lo que probaste antes probablemente fueron soluciones aisladas: una pastilla, un audio, un tip. Pero el insomnio por carga mental no se resuelve con una sola herramienta. Este protocolo no te da más cosas que probar: te da una secuencia de señales que tu sistema nervioso puede reconocer y repetir. La diferencia está en la estructura. No buscamos que una noche funcione. Buscamos que tu cuerpo aprenda a anticipar el descanso. Y eso solo ocurre cuando hay un patrón claro, sostenido y realista.'
  }, {
    question: 'Mi problema no es dormirme, es que me despierto a las 3 o 4 de la madrugada y ya no puedo volver a dormir. ¿Esto me sirve?',
    answer: 'Sí. El protocolo incluye un plan específico para ese momento: el Plan 3:00am. No se trata de forzarte a dormir ni de quedarte mirando el techo negociando con el reloj. Es una secuencia corta de pasos que evita que tu mente entre en modo alerta y te ayuda a volver al sueño sin convertir la madrugada en una batalla. Muchas mujeres que usan este protocolo reportan que los despertares nocturnos no desaparecen de inmediato, pero dejan de ser una crisis.'
  }, {
    question: 'No tengo tiempo ni energía para rutinas largas. ¿Cuánto tiempo me va a llevar esto cada noche?',
    answer: 'El protocolo completo toma entre 7 y 12 minutos. Pero si una noche no puedes hacer todo, hay versiones mínimas de 3 minutos que mantienen la señal activa. Este método no está diseñado para personas con vidas perfectas. Está diseñado para mujeres reales, con agendas llenas, que necesitan algo que funcione incluso en sus peores semanas. Lo importante no es hacerlo perfecto. Es hacerlo consistente.'
  }, {
    question: '¿Qué pasa si una noche no puedo seguir el protocolo completo o me lo salto?',
    answer: 'No pasa nada grave. Este no es un sistema de todo o nada. Si una noche no puedes, usas el Plan B de 3 minutos. Si te lo saltas, al día siguiente retomas sin culpa ni reinicio. El protocolo incluye una guía de mantenimiento que te enseña exactamente cómo volver después de una mala noche, sin sentir que perdiste el progreso. La perfección no es el objetivo. La repetición flexible, sí.'
  }, {
    question: '¿Para quién es este protocolo y para quién no?',
    answer: 'Es para ti si te acuestas cansada pero tu mente no para, si te despiertas en la madrugada con la cabeza activa, o si sientes que el mal dormir ya está afectando tu humor, tu paciencia y tu día. No es para ti si sospechas que tienes apnea del sueño sin tratar, si estás atravesando una crisis emocional severa, o si necesitas atención médica urgente. Este protocolo es una guía de bienestar, no un tratamiento clínico. Si tienes dudas, consulta con un profesional de salud antes de comenzar.'
  }];
  return <div className="py-20 px-4 sm:px-6 bg-cream-50" data-id="element-49">
      <div className="max-w-3xl mx-auto" data-id="element-50">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-51">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-900 mb-4" data-id="element-52">
            Preguntas Frecuentes
          </h2>
          <p className="text-lg text-navy-600" data-id="element-53">
            Respuestas claras a las dudas más comunes antes de comenzar
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-2xl shadow-lg border border-cream-200 p-6 sm:p-8" data-id="element-54">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-55" />)}
        </motion.div>
      </div>
    </div>;
}